import React, { startTransition, useDeferredValue } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";

import { psychologistsApi } from "../api/psychologistsApi";
import { dictApi } from "../api/dictApi";
import { useAuth } from "../auth/authStore";
import AppSidebarV2 from "../components/layout/AppSidebarV2";
import { useToast } from "../ui/toast/ToastProvider";
import { buildPsychologistProfilePath } from "../utils/psychologistProfile";

const TABS = {
  ALL: "all",
  GROUPS: "groups",
  PSYCHOLOGISTS: "psychs",
};

const SORT_OPTIONS = [
  { value: "rating", label: "По рейтингу" },
  { value: "price-asc", label: "Сначала дешевле" },
  { value: "price-desc", label: "Сначала дороже" },
  { value: "experience", label: "По опыту" },
  { value: "name", label: "По имени" },
];

const EXPERIENCE_OPTIONS = ["До 3 лет", "3-7 лет", "8-12 лет", "13+ лет"];
const PRICE_OPTIONS = ["До 3 000 ₽", "3 000–4 000 ₽", "4 000–5 000 ₽", "От 5 000 ₽"];
const PAGE_SIZE = 100;
const CARD_PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='1067'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop stop-color='%23d7d8ff'/%3E%3Cstop offset='1' stop-color='%23f6f7ff'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23g)'/%3E%3C/svg%3E";
const FALLBACK_GRADIENTS = [
  "linear-gradient(160deg, #B9B0E8 0%, #DAD4F3 55%, #EDEAFA 100%)",
  "linear-gradient(160deg, #7B6FD4 0%, #A89DE8 55%, #C7BFEF 100%)",
  "linear-gradient(160deg, #6FA8D4 0%, #9BC4E0 55%, #C8E0EE 100%)",
  "linear-gradient(160deg, #5AEAA0 0%, #9FE8C6 55%, #D3F4E3 100%)",
  "linear-gradient(160deg, #2B2B2B 0%, #4A4A4A 55%, #6E6E6E 100%)",
  "linear-gradient(160deg, #A89DE8 0%, #5AEAA0 120%)",
];

function safeText(value) {
  return String(value ?? "").trim();
}

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
}

function normalizeComparable(value) {
  return safeText(value).toLowerCase();
}

function extractItems(raw) {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.items)) return raw.items;
  if (Array.isArray(raw?.content)) return raw.content;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
}

function splitLabelTokens(values) {
  const tokens = toArray(values)
    .flatMap((value) => {
      if (!value) return [];
      if (typeof value === "string") return String(value).split(/[,\n;•]+/g);
      const label = value?.title || value?.name || value?.label || value?.value || "";
      return String(label).split(/[,\n;•]+/g);
    })
    .map((value) => safeText(value))
    .filter(Boolean);
  return Array.from(new Set(tokens));
}

function firstLabelFrom(values, fallback = "") {
  const list = splitLabelTokens(values);
  return list[0] || fallback;
}

function formatMoneyLabel(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return "По запросу";
  return `${num.toLocaleString("ru-RU")} ₽`;
}

function formatYearsRu(value) {
  const years = Math.abs(Number(value));
  if (!Number.isFinite(years) || years <= 0) return "—";
  const mod10 = years % 10;
  const mod100 = years % 100;
  if (mod100 >= 11 && mod100 <= 14) return `${years} лет`;
  if (mod10 === 1) return `${years} год`;
  if (mod10 >= 2 && mod10 <= 4) return `${years} года`;
  return `${years} лет`;
}

function toDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatTimeLabel(value) {
  const date = toDate(value);
  if (!date) return "";
  return date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

function normalizeSlotsFromApi(rawSlots) {
  const parsed = [];
  toArray(rawSlots).forEach((slot) => {
    if (!slot) return;
    if (typeof slot === "string") {
      const date = toDate(slot);
      if (date) {
        const day = date.toLocaleDateString("ru-RU", { weekday: "short" }).replace(".", "");
        const time = formatTimeLabel(slot);
        parsed.push(`${day} ${time}`);
      } else {
        parsed.push(safeText(slot));
      }
      return;
    }

    const start = slot?.startAt || slot?.startDateTime || slot?.start || slot?.dateTime || slot?.datetime;
    if (start) {
      const date = toDate(start);
      if (date) {
        const day = date.toLocaleDateString("ru-RU", { weekday: "short" }).replace(".", "");
        const time = formatTimeLabel(start);
        parsed.push(`${day} ${time}`);
        return;
      }
    }

    const dayLabel = safeText(slot?.day || slot?.dayLabel || slot?.date || "");
    const timeLabel = safeText(slot?.time || slot?.timeLabel || "");
    const joined = safeText(`${dayLabel} ${timeLabel}`);
    if (joined) parsed.push(joined);
  });

  return Array.from(new Set(parsed.map((value) => safeText(value)).filter(Boolean))).slice(0, 6);
}

function parseDurationMinutes(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) && value > 0 ? Math.round(value) : 0;

  const text = safeText(value);
  if (!text) return 0;

  const numeric = Number(text);
  if (Number.isFinite(numeric) && numeric > 0) return Math.round(numeric);

  const hhmmss = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (hhmmss) {
    return (Number(hhmmss[1]) || 0) * 60 + (Number(hhmmss[2]) || 0);
  }

  const iso = text.match(/^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/i);
  if (iso) {
    const hours = Number(iso[1] || 0);
    const mins = Number(iso[2] || 0);
    const secs = Number(iso[3] || 0);
    return hours * 60 + mins + (secs >= 30 ? 1 : 0);
  }

  return 0;
}

function resolveDurationMinutes(raw) {
  const candidates = [
    raw?.sessionDurationMinutes,
    raw?.session_duration_minutes,
    raw?.sessionDuration,
    raw?.durationMinutes,
    raw?.duration_minutes,
    raw?.booking?.sessionDurationMinutes,
    raw?.booking?.sessionDuration,
    raw?.booking?.durationMinutes,
    raw?.schedule?.slotMinutes,
    raw?.flexibleSchedule?.slotMinutes,
  ];

  for (const candidate of candidates) {
    const minutes = parseDurationMinutes(candidate);
    if (minutes > 0) return minutes;
  }
  return 0;
}

function mapPsychologistCard(raw, index = 0) {
  const themes = splitLabelTokens(raw?.themes || raw?.themeLabels || raw?.topics);
  const methods = splitLabelTokens(raw?.methods || raw?.methodLabels || raw?.method);
  const method = firstLabelFrom(methods, safeText(raw?.method));
  const experienceYears = Number(raw?.experienceYears ?? raw?.expYears ?? 0) || 0;
  const priceValue = Number(raw?.pricePerSession ?? raw?.price ?? raw?.priceValue ?? 0) || 0;
  const duration = resolveDurationMinutes(raw);
  const rating = Number(raw?.averageRating ?? raw?.rating ?? raw?.ratingValue);
  const reviews = Number(raw?.reviewsCount ?? raw?.reviews ?? raw?.feedbackCount);
  const slots = normalizeSlotsFromApi(raw?.nextSlots || raw?.availableSlots || raw?.slots || raw?.calendarPreview);
  const onlineRaw =
    raw?.isOnline ??
    raw?.is_online ??
    raw?.online ??
    raw?.user?.isOnline ??
    raw?.user?.is_online ??
    raw?.user?.online ??
    null;
  const hasOnlineStatus = onlineRaw !== null && onlineRaw !== undefined && String(onlineRaw).trim() !== "";
  const isOnline = hasOnlineStatus ? Boolean(onlineRaw) : null;

  return {
    id: raw?.id ?? raw?.userId ?? `psy-${index}`,
    userId: raw?.userId ?? raw?.id ?? null,
    name: safeText(raw?.name || raw?.fullName || raw?.title),
    statusLabel: hasOnlineStatus ? (isOnline ? "Онлайн" : "Офлайн") : "Статус не указан",
    isOnline,
    ratingLabel: Number.isFinite(rating) && rating > 0 ? rating.toFixed(1) : "—",
    reviewsLabel: Number.isFinite(reviews) && reviews >= 0 ? String(reviews) : "0",
    specializationLabel: [method, experienceYears > 0 ? `${experienceYears}+ лет` : ""].filter(Boolean).join(" · "),
    priceLabel: formatMoneyLabel(priceValue),
    priceValue,
    experienceYears,
    method,
    methods,
    themes,
    durationLabel: duration > 0 ? `/ ${duration} мин` : "",
    durationMinutes: duration,
    imageUrl: safeText(raw?.avatarUrl || raw?.imageUrl || raw?.photoUrl || ""),
    about: safeText(raw?.about || raw?.headline || raw?.description),
    isFavorite: Boolean(raw?.isFavorite),
    averageRating: Number.isFinite(rating) ? rating : null,
    reviewsCount: Number.isFinite(reviews) ? reviews : 0,
    slots,
    gradient: FALLBACK_GRADIENTS[index % FALLBACK_GRADIENTS.length],
  };
}

function getPsychSlots(item) {
  return (item?.slots || [])
    .filter((slot) => String(slot).includes(":"))
    .map((slot) => {
      const parts = String(slot).trim().split(/\s+/);
      const timeLabel = parts.pop() || "";
      const dayLabel = parts.join(" ") || "Ближайший";
      return {
        key: `${dayLabel}-${timeLabel}`,
        dayLabel,
        timeLabel,
        fullLabel: `${dayLabel}, ${timeLabel}`,
      };
    });
}

function countLabel(value) {
  const count = Math.max(0, Number(value) || 0);
  const mod10 = count % 10;
  const mod100 = count % 100;
  let suffix = "результатов";
  if (mod10 === 1 && mod100 !== 11) suffix = "результат";
  else if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) suffix = "результата";
  return `${count} ${suffix}`;
}

function toFavoriteKey(id) {
  return String(id ?? "");
}

function HeartIcon({ active = false }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"
        fill={active ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.6" y2="16.6" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function SortSelect({ value, onChange }) {
  return (
    <label className="psych-finder__sort">
      <span>{SORT_OPTIONS.find((item) => item.value === value)?.label || "По рейтингу"}</span>
      <ChevronIcon />
      <select value={value} onChange={(event) => onChange(event.target.value)} aria-label="Сортировка">
        {SORT_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function FilterChip({ label, value, options, onChange, allLabel }) {
  return (
    <label className={`psych-finder__filter-chip ${value ? "is-active" : ""}`}>
      <span>{value || label}</span>
      <ChevronIcon />
      <select value={value} onChange={(event) => onChange(event.target.value)} aria-label={label}>
        <option value="">{allLabel || label}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function Psychologists() {
  const navigate = useNavigate();
  const toast = useToast();
  const { isAuthenticated } = useAuth();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(TABS.ALL);
  const [searchValue, setSearchValue] = React.useState("");
  const [sortValue, setSortValue] = React.useState("rating");
  const [favoriteOnly, setFavoriteOnly] = React.useState(false);
  const [themeFilter, setThemeFilter] = React.useState("");
  const [methodFilter, setMethodFilter] = React.useState("");
  const [experienceFilter, setExperienceFilter] = React.useState("");
  const [priceFilter, setPriceFilter] = React.useState("");
  const [dictThemes, setDictThemes] = React.useState([]);
  const [dictMethods, setDictMethods] = React.useState([]);
  const [themeOptions, setThemeOptions] = React.useState([]);
  const [methodOptions, setMethodOptions] = React.useState([]);
  const [selectedThemeDict, setSelectedThemeDict] = React.useState(null);
  const [selectedMethodDict, setSelectedMethodDict] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [errorText, setErrorText] = React.useState("");
  const [specialists, setSpecialists] = React.useState([]);
  const [favoritePsychIds, setFavoritePsychIds] = React.useState(() => new Set());
  const [modalItem, setModalItem] = React.useState(null);
  const [selectedModalSlot, setSelectedModalSlot] = React.useState("");
  const deferredSearch = useDeferredValue(searchValue);

  const query = safeText(deferredSearch);
  const modalOpen = Boolean(modalItem);
  const modalSlots = React.useMemo(() => getPsychSlots(modalItem), [modalItem]);
  const modalFavoriteActive = React.useMemo(
    () => (modalItem ? favoritePsychIds.has(toFavoriteKey(modalItem.userId || modalItem.id)) : false),
    [favoritePsychIds, modalItem]
  );
  const visibleCountBase = favoriteOnly
    ? specialists.filter((item) => favoritePsychIds.has(toFavoriteKey(item.userId || item.id))).length
    : specialists.length;
  const tabAllCount = visibleCountBase;
  const tabPsychCount = visibleCountBase;
  const tabGroupsCount = 0;

  React.useEffect(() => {
    if (!isAuthenticated) return undefined;
    let alive = true;

    psychologistsApi
      .favourites()
      .then((payload) => {
        if (!alive) return;
        const next = new Set(
          extractItems(payload)
            .map((id) => toFavoriteKey(id))
            .filter(Boolean)
        );
        setFavoritePsychIds(next);
      })
      .catch(() => {});

    return () => {
      alive = false;
    };
  }, [isAuthenticated]);

  React.useEffect(() => {
    let alive = true;

    Promise.allSettled([dictApi.themes(), dictApi.methods()]).then(([themesResult, methodsResult]) => {
      if (!alive) return;
      if (themesResult.status === "fulfilled") {
        const nextThemes = extractItems(themesResult.value);
        setDictThemes(nextThemes);
        setThemeOptions(Array.from(new Set(nextThemes.map((item) => safeText(item?.title)).filter(Boolean))));
      }
      if (methodsResult.status === "fulfilled") {
        const nextMethods = extractItems(methodsResult.value);
        setDictMethods(nextMethods);
        setMethodOptions(Array.from(new Set(nextMethods.map((item) => safeText(item?.title)).filter(Boolean))));
      }
    });

    return () => {
      alive = false;
    };
  }, []);

  React.useEffect(() => {
    setSelectedThemeDict(
      dictThemes.find((item) => normalizeComparable(item?.title) === normalizeComparable(themeFilter)) || null
    );
    setSelectedMethodDict(
      dictMethods.find((item) => normalizeComparable(item?.title) === normalizeComparable(methodFilter)) || null
    );
  }, [dictMethods, dictThemes, methodFilter, themeFilter]);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    setErrorText("");

    psychologistsApi
      .list({
        page: 0,
        size: PAGE_SIZE,
        q: query,
        themes: selectedThemeDict?.id ? [Number(selectedThemeDict.id)] : [],
        methods: selectedMethodDict?.id ? [Number(selectedMethodDict.id)] : [],
        experience: experienceFilter ? [experienceFilter] : [],
        price: priceFilter ? [priceFilter] : [],
        sort: "rating,desc",
      })
      .then((response) => {
        if (!alive) return;
        const items = extractItems(response).map((item, index) => mapPsychologistCard(item, index));
        setSpecialists(items);
        setFavoritePsychIds((prev) => {
          const next = new Set(prev);
          items.filter((item) => item.isFavorite).forEach((item) => next.add(toFavoriteKey(item.userId || item.id)));
          return next;
        });
      })
      .catch(() => {
        if (!alive) return;
        setSpecialists([]);
        setErrorText("Не удалось загрузить каталог специалистов.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [experienceFilter, priceFilter, query, selectedMethodDict?.id, selectedThemeDict?.id]);

  React.useEffect(() => {
    if (!modalOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const handleEscape = (event) => {
      if (event.key === "Escape") setModalItem(null);
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [modalOpen]);

  const sortedSpecialists = React.useMemo(() => {
    const list = favoriteOnly
      ? specialists.filter((item) => favoritePsychIds.has(toFavoriteKey(item.userId || item.id)))
      : [...specialists];

    const sorted = [...list];
    sorted.sort((left, right) => {
      if (sortValue === "price-asc") return (left.priceValue || 0) - (right.priceValue || 0);
      if (sortValue === "price-desc") return (right.priceValue || 0) - (left.priceValue || 0);
      if (sortValue === "experience") return (right.experienceYears || 0) - (left.experienceYears || 0);
      if (sortValue === "name") return String(left.name || "").localeCompare(String(right.name || ""), "ru");

      const ratingDiff = (right.averageRating || 0) - (left.averageRating || 0);
      if (ratingDiff !== 0) return ratingDiff;
      const reviewsDiff = (right.reviewsCount || 0) - (left.reviewsCount || 0);
      if (reviewsDiff !== 0) return reviewsDiff;
      return (left.priceValue || 0) - (right.priceValue || 0);
    });
    return sorted;
  }, [favoriteOnly, favoritePsychIds, sortValue, specialists]);

  const visibleSpecialists = activeTab === TABS.GROUPS ? [] : sortedSpecialists;
  const countText = countLabel(visibleSpecialists.length);

  const closeModal = () => setModalItem(null);

  const openPsychModal = (item) => {
    const slots = getPsychSlots(item);
    setSelectedModalSlot(slots[0]?.fullLabel || "");
    setModalItem(item);
  };

  const togglePsychFavorite = async (psychId) => {
    const key = toFavoriteKey(psychId);
    let wasFavorite = false;

    setFavoritePsychIds((prev) => {
      const next = new Set(prev);
      wasFavorite = next.has(key);
      if (wasFavorite) next.delete(key);
      else next.add(key);
      return next;
    });

    try {
      if (wasFavorite) await psychologistsApi.removeFavourite(psychId);
      else await psychologistsApi.addFavourite(psychId);
    } catch {
      setFavoritePsychIds((prev) => {
        const next = new Set(prev);
        if (wasFavorite) next.add(key);
        else next.delete(key);
        return next;
      });
      toast.error("Не удалось обновить избранное.");
    }
  };

  const onCardKeyDown = (event, item) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openPsychModal(item);
    }
  };

  return (
    <div className="podcasts-hub psych-finder-auth-shell">
      <Helmet prioritizeSeoTags>
        <title>Подобрать психолога — кабинет | Бюро Души</title>
        <meta
          name="description"
          content="Подбор психолога в авторизованном кабинете: поиск по теме, методу, опыту и стоимости."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="podcasts-shell psych-finder-shell">
        <AppSidebarV2
          activeKey="catalog"
          mobileOpen={sidebarOpen}
          onCloseMobile={() => setSidebarOpen(false)}
        />

        <button
          className={`podcasts-overlay ${sidebarOpen ? "is-open" : ""}`}
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setSidebarOpen(false)}
        />

        <div className="psych-finder-main">
          <div className="psych-finder-mobilebar">
            <div className="psych-finder-mobilebar__left">
              <button
                className="psych-finder-mobilebar__burger"
                type="button"
                onClick={() => setSidebarOpen(true)}
                aria-label="Открыть меню"
              >
                ☰
              </button>
              <div className="psych-finder-mobilebar__brand">
                <span className="psych-finder-mobilebar__brand-dot" />
                <span className="psych-finder-mobilebar__brand-text">Бюро Души</span>
              </div>
            </div>
          </div>

          <header className="psych-finder-topbar">
            <div className="psych-finder-topbar__titles">
              <div className="psych-finder-topbar__title">Подобрать психолога</div>
              <div className="psych-finder-topbar__sub">
                Выберите специалиста, формат встречи и удобную стоимость.
              </div>
            </div>
            <div className="psych-finder-topbar__actions">
              <button
                type="button"
                className={`psych-finder-topbar__btn ${favoriteOnly ? "is-active" : ""}`}
                onClick={() => setFavoriteOnly((prev) => !prev)}
              >
                <HeartIcon active={favoriteOnly} />
                <span>Избранные</span>
              </button>
              <button
                type="button"
                className="psych-finder-topbar__btn"
                onClick={() => navigate("/my-appointments")}
              >
                <span>Мои записи</span>
              </button>
            </div>
          </header>

          <main className="psych-finder-content">
            <div className="psych-finder-mobile-title">
              <div className="psych-finder-mobile-title__title">Подобрать психолога</div>
              <div className="psych-finder-mobile-title__sub">
                Выберите специалиста, формат встречи и удобную стоимость.
              </div>
            </div>

            <div className="psych-finder-tabs" role="tablist" aria-label="Режим каталога">
              <button
                type="button"
                className={`psych-finder-tab ${activeTab === TABS.ALL ? "is-active" : ""}`}
                onClick={() => startTransition(() => setActiveTab(TABS.ALL))}
              >
                <span>Все</span>
                <span className="psych-finder-tab__count">{tabAllCount}</span>
              </button>
              <button
                type="button"
                className={`psych-finder-tab ${activeTab === TABS.GROUPS ? "is-active" : ""}`}
                onClick={() => startTransition(() => setActiveTab(TABS.GROUPS))}
              >
                <span>Групповые</span>
                <span className="psych-finder-tab__count">{tabGroupsCount}</span>
              </button>
              <button
                type="button"
                className={`psych-finder-tab ${activeTab === TABS.PSYCHOLOGISTS ? "is-active" : ""}`}
                onClick={() => startTransition(() => setActiveTab(TABS.PSYCHOLOGISTS))}
              >
                <span>Специалисты</span>
                <span className="psych-finder-tab__count">{tabPsychCount}</span>
              </button>
            </div>

            <div className="psych-finder-search-row">
              <label className="psych-finder-searchbox">
                <SearchIcon />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  placeholder="Имя, метод, тема…"
                />
              </label>
              <SortSelect value={sortValue} onChange={setSortValue} />
            </div>

            <div className="psych-finder-filters">
              <FilterChip
                label="Тема"
                value={themeFilter}
                options={themeOptions}
                onChange={(nextValue) => startTransition(() => setThemeFilter(nextValue))}
                allLabel="Все темы"
              />
              <FilterChip
                label="Метод"
                value={methodFilter}
                options={methodOptions}
                onChange={(nextValue) => startTransition(() => setMethodFilter(nextValue))}
                allLabel="Все методы"
              />
              <FilterChip
                label="Опыт"
                value={experienceFilter}
                options={EXPERIENCE_OPTIONS}
                onChange={(nextValue) => startTransition(() => setExperienceFilter(nextValue))}
                allLabel="Любой опыт"
              />
              <FilterChip
                label="Цена"
                value={priceFilter}
                options={PRICE_OPTIONS}
                onChange={(nextValue) => startTransition(() => setPriceFilter(nextValue))}
                allLabel="Любая цена"
              />
            </div>

            {activeTab === TABS.GROUPS ? (
              <section className="psych-finder-group-block">
                <div className="psych-finder-section-row">
                  <div className="psych-finder-section-label">Групповые сессии</div>
                </div>
                <div className="psych-finder-empty">
                  <span>Пока нет доступных групповых сессий.</span>
                </div>
              </section>
            ) : (
              <section>
                <div className="psych-finder-section-row">
                  <div className="psych-finder-section-label">Специалисты</div>
                  <div className="psych-finder-section-count">{countText}</div>
                </div>

                {errorText ? <div className="psych-finder-empty is-error">{errorText}</div> : null}
                {loading ? <div className="psych-finder-empty">Загружаем карточки специалистов…</div> : null}
                {!loading && !errorText && !visibleSpecialists.length ? (
                  <div className="psych-finder-empty">
                    {favoriteOnly ? "В избранном пока нет специалистов." : "Ничего не найдено по этому запросу."}
                  </div>
                ) : null}

                {!loading && !errorText && visibleSpecialists.length ? (
                  <div className="psych-finder-grid">
                    {visibleSpecialists.map((item) => {
                      const favKey = toFavoriteKey(item.userId || item.id);
                      const isFav = favoritePsychIds.has(favKey);
                      return (
                        <article
                          key={item.id}
                          className="psych-finder-card"
                          role="button"
                          tabIndex={0}
                          onClick={() => openPsychModal(item)}
                          onKeyDown={(event) => onCardKeyDown(event, item)}
                          aria-label={`Открыть карточку психолога ${item.name}`}
                        >
                          {item.imageUrl ? (
                            <img className="psych-finder-card__photo" src={item.imageUrl} loading="lazy" alt={item.name} />
                          ) : (
                            <div
                              className="psych-finder-card__photo psych-finder-card__photo--fallback"
                              style={{ backgroundImage: item.gradient || `url(${CARD_PLACEHOLDER_IMAGE})` }}
                              aria-hidden="true"
                            />
                          )}
                          <div className="psych-finder-card__shade" />
                          <div className="psych-finder-card__status">
                            <span className={`psych-finder-card__status-dot ${item.isOnline === true ? "is-online" : ""}`} />
                            {item.statusLabel}
                          </div>
                          <button
                            type="button"
                            className={`psych-finder-card__fav ${isFav ? "is-active" : ""}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              togglePsychFavorite(item.userId || item.id);
                            }}
                            aria-label={isFav ? "Убрать из избранного" : "Добавить в избранное"}
                          >
                            <HeartIcon active={isFav} />
                          </button>

                          <div className="psych-finder-card__info">
                            <div className="psych-finder-card__rating">
                              <span className="psych-finder-card__stars">★★★★★</span>
                              <span className="psych-finder-card__reviews">
                                {item.ratingLabel} ({item.reviewsLabel})
                              </span>
                            </div>
                            <div className="psych-finder-card__name">{item.name}</div>
                            <div className="psych-finder-card__method">{item.specializationLabel}</div>
                            <div className="psych-finder-card__footer">
                              <div>
                                <div className="psych-finder-card__price">{item.priceLabel}</div>
                                <div className="psych-finder-card__duration">
                                  {item.durationMinutes > 0 ? `${item.durationMinutes} мин` : "Индивидуальная сессия"}
                                </div>
                              </div>
                              <span className="psych-finder-card__go">
                                <ArrowIcon />
                              </span>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                ) : null}
              </section>
            )}
          </main>
        </div>
      </div>

      {modalOpen && modalItem ? (
        <div
          className="psych-finder-modal"
          role="dialog"
          aria-modal="true"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeModal();
          }}
        >
          <div className="psych-finder-modal__card">
            <div className="psych-finder-modal__photo-col">
              {modalItem.imageUrl ? (
                <img className="psych-finder-modal__photo" src={modalItem.imageUrl} alt={modalItem.name} />
              ) : (
                <div
                  className="psych-finder-modal__photo psych-finder-modal__photo--fallback"
                  style={{ backgroundImage: modalItem.gradient || `url(${CARD_PLACEHOLDER_IMAGE})` }}
                  aria-hidden="true"
                />
              )}
              <div className="psych-finder-modal__photo-shade" />
              <button type="button" className="psych-finder-modal__close" onClick={closeModal} aria-label="Закрыть">
                ✕
              </button>
              <div className="psych-finder-modal__photo-info">
                <div className="psych-finder-modal__name">{modalItem.name}</div>
                <div className="psych-finder-modal__badge">
                  Индивидуальная{modalItem.durationMinutes > 0 ? ` · ${modalItem.durationMinutes} мин` : ""}
                </div>
                <div className="psych-finder-modal__price">{modalItem.priceLabel}</div>
                <div className="psych-finder-modal__price-note">за сессию</div>
              </div>
            </div>

            <div className="psych-finder-modal__right">
              <div className="psych-finder-modal__body">
                <div className="psych-finder-modal__row">
                  <div className="psych-finder-modal__row-label">Время</div>
                  <div className="psych-finder-modal__row-value">{selectedModalSlot || "Выберите слот ниже"}</div>
                </div>
                <div className="psych-finder-modal__row">
                  <div className="psych-finder-modal__row-label">Опыт</div>
                  <div className="psych-finder-modal__row-value">{formatYearsRu(modalItem.experienceYears)}</div>
                </div>
                <div className="psych-finder-modal__row">
                  <div className="psych-finder-modal__row-label">Метод</div>
                  <div className="psych-finder-modal__row-value">
                    {(modalItem.methods && modalItem.methods.length ? modalItem.methods : [modalItem.method])
                      .filter(Boolean)
                      .map((method) => (
                        <span key={method} className="psych-finder-modal__tag psych-finder-modal__tag--lav">
                          {method}
                        </span>
                      ))}
                  </div>
                </div>
                <div className="psych-finder-modal__row">
                  <div className="psych-finder-modal__row-label">Темы</div>
                  <div className="psych-finder-modal__row-value psych-finder-modal__topics">
                    {(modalItem.themes || []).length ? (
                      modalItem.themes.map((topic) => (
                        <span key={topic} className="psych-finder-modal__tag psych-finder-modal__tag--mint">
                          {topic}
                        </span>
                      ))
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                </div>
                <div className="psych-finder-modal__row psych-finder-modal__row--last">
                  <div className="psych-finder-modal__row-label">О себе</div>
                  <div className="psych-finder-modal__row-value psych-finder-modal__about">
                    {modalItem.about || "Описание пока не добавлено."}
                  </div>
                </div>

                <div className="psych-finder-modal__slots">
                  <div className="psych-finder-modal__slots-label">Выберите слот</div>
                  {modalSlots.length ? (
                    <div className="psych-finder-modal__slots-grid">
                      {modalSlots.map((slot) => (
                        <button
                          key={slot.key}
                          type="button"
                          className={`psych-finder-modal__slot ${selectedModalSlot === slot.fullLabel ? "is-selected" : ""}`}
                          onClick={() => setSelectedModalSlot(slot.fullLabel)}
                        >
                          <div className="psych-finder-modal__slot-time">{slot.timeLabel}</div>
                          <div className="psych-finder-modal__slot-day">{slot.dayLabel}</div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="psych-finder-modal__slots-empty">
                      Нет свободных слотов — можно перейти на страницу психолога и записаться там.
                    </div>
                  )}
                </div>
              </div>

              <div className="psych-finder-modal__footer">
                <div className="psych-finder-modal__footer-price">
                  <div className="psych-finder-modal__footer-value">{modalItem.priceLabel}</div>
                  <div className="psych-finder-modal__footer-label">за сессию</div>
                </div>
                <div className="psych-finder-modal__footer-actions">
                  <button
                    type="button"
                    className={`psych-finder-modal__fav-btn ${modalFavoriteActive ? "is-active" : ""}`}
                    onClick={() => togglePsychFavorite(modalItem.userId || modalItem.id)}
                  >
                    <HeartIcon active={modalFavoriteActive} />
                    <span>{modalFavoriteActive ? "В избранном" : "+ В избранное"}</span>
                  </button>
                  <Link
                    className="psych-finder-modal__open-btn"
                    to={buildPsychologistProfilePath(modalItem.userId || modalItem.id)}
                    onClick={closeModal}
                  >
                    Открыть профиль
                    <ArrowIcon />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

    </div>
  );
}
